import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { KpiHintContent } from './kpiHints';

/**
 * Поповер-подсказка KPI-карточки: иконка ⓘ, объяснение + арифметика.
 * Портал к body — root карточки имеет overflow-hidden (конвенция Combobox).
 * Десктоп: hover; тач/клавиатура: клик-toggle, Escape/клик вне закрывают.
 */
export function KpiHint({ content }: { content: KpiHintContent }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const place = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.left, window.innerWidth - 300)) });
  };

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (btnRef.current && e.target instanceof Node && btnRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="Что это значит"
        className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full text-[10px] leading-none text-text-subtle transition-colors hover:text-accent"
        onMouseEnter={() => {
          place();
          setOpen(true);
        }}
        onMouseLeave={() => setOpen(false)}
        onClick={() => {
          // Не toggle: на таче браузер синтезирует mouseenter ПЕРЕД click —
          // toggle схлопывал бы первый тап в «открыть и сразу закрыть».
          // Закрытие — клик вне / Escape / mouseleave.
          place();
          setOpen(true);
        }}
      >
        ⓘ
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            className="fixed z-30 w-[280px] rounded-lg border border-border bg-bg2 p-3 text-left shadow-lg"
            style={{ top: pos.top, left: pos.left }}
          >
            <div className="text-xs leading-snug text-text2 normal-case tracking-normal">{content.explain}</div>
            {content.math && (
              <div className="mt-2 border-t border-border pt-2 font-mono text-[11px] tabular-nums text-text normal-case tracking-normal">
                {content.math}
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
